import { SyncItemType, generateId, toDateString } from "@taskfortress/core"
import type { Tag, Task } from "@taskfortress/core/src/clientData/contentTypes"
import { TaskStatus } from "@taskfortress/core/src/clientData/contentTypes"
import React, { useCallback, useMemo, useState } from "react"
import { useData } from "../providers/DataProvider"
import { cn } from "../styles/utils"
import { FloatingActionButton } from "./FloatingActionButton"
import { Icon } from "./Icons"
import { Pressable } from "./Pressable"
import { Tooltip } from "./Tooltip"
import { TaskModal } from "./tasks/TaskModal"

interface AddTaskButtonProps {
  date: Date | string
  selectedTag?: Tag | null
  className?: string
  small?: boolean
}

export const AddTaskButton: React.FC<AddTaskButtonProps> = ({
  date,
  selectedTag,
  className,
  small = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { saveItems } = useData()

  // Create a new task template
  const newTask: Task = useMemo(() => {
    const task = {
      id: "",
      title: "",
      description: "",
      tags: selectedTag ? [selectedTag.name] : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      dueDate: toDateString(date),
      status: TaskStatus.TODO,
      index: 0,
    }

    return task
  }, [date, selectedTag])

  // Handle opening the modal
  const handlePress = useCallback(() => {
    setIsModalVisible(true)
  }, [])

  // Handle closing the modal
  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false)
  }, [])

  // Handle saving the task
  const handleSaveTask = useCallback(
    async (task: Task) => {
      try {
        // Generate a new ID for the task
        const taskId = generateId()

        // Save the task using the DataProvider
        await saveItems([
          {
            itemId: taskId,
            type: SyncItemType.Task,
            content: {
              title: task.title,
              description: task.description,
              status: task.status,
              tags: task.tags,
              dueDate: task.dueDate,
            },
          },
        ])

        setIsModalVisible(false)
      } catch (error) {
        console.error("Failed to save task:", error)
      }
    },
    [saveItems],
  )

  // Memoize the icon prop
  const icon = useMemo(
    () => <Icon name="plus" className="text-white h-6 w-6" />,
    [],
  )

  // Memoize hitSlop object for small button
  const hitSlop = useMemo(() => ({ top: 8, bottom: 8, left: 8, right: 8 }), [])

  // Render small button variant
  if (small) {
    return (
      <>
        <Tooltip content="Add task" placement="bottom">
          <Pressable
            onPress={handlePress}
            className={cn(
              "w-6 h-6 rounded-full bg-primary-500 items-center justify-center",
              className,
            )}
            hitSlop={hitSlop}
          >
            <Icon name="plus" className="text-white h-3 w-3" />
          </Pressable>
        </Tooltip>
        <TaskModal
          task={newTask}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          onSave={handleSaveTask}
        />
      </>
    )
  }

  // Render regular floating action button
  return (
    <>
      <Tooltip content="Add task" placement="top">
        <FloatingActionButton
          icon={icon}
          onPress={handlePress}
          className={className}
        />
      </Tooltip>
      <TaskModal
        task={newTask}
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSave={handleSaveTask}
      />
    </>
  )
}
